import React, { createContext, useContext, useEffect, useState } from 'react';
import { windowIsDefined } from 'app/helpers/windowIsDefined';
import PostMessageEventName from 'app/types/native/PostMessageEventName';
const isNativeApp = () => {
  if (windowIsDefined) {
    var _window$webkit;
    // Legacy Native: iOS (Deprecated)
    if ((_window$webkit = window.webkit) !== null && _window$webkit !== void 0 && _window$webkit.messageHandlers.native) {
      return window.webkit.messageHandlers.native;
    }
    if (window.native) {
      // Legacy Native: Android postMessage channel (Deprecated)
      // Flutter Core WebView: android & ios postMessage channel available on window.native
      return window.native;
    }
  }
  return null;
};
const generateHandler = key => {
  if (isNativeApp()) {
    return value => {
      const isFlutterApp = isNativeFlutterApp();
      const handlerName = isFlutterApp && key === 'showNativeNotificationPermissionDialog' ? 'getNativeNotificationPermission' : key;
      const message = value ? "".concat(handlerName, ":").concat(value) : handlerName;
      isNativeApp().postMessage(message);
    };
  }
  return () => {};
};
const availableHandlers = {
  logout: {
    name: PostMessageEventName.Logout,
    handler: generateHandler(PostMessageEventName.Logout)
  },
  openDeviceSettings: {
    name: PostMessageEventName.OpenDeviceSettings,
    handler: generateHandler(PostMessageEventName.OpenDeviceSettings)
  },
  triggerAppTrackingTransparencyDialog: {
    name: PostMessageEventName.TriggerAppTrackingTransparencyDialog,
    handler: generateHandler(PostMessageEventName.TriggerAppTrackingTransparencyDialog)
  },
  shareUrl: {
    name: PostMessageEventName.ShareUrl,
    handler: generateHandler(PostMessageEventName.ShareUrl)
  },
  triggerRatingDialog: {
    name: PostMessageEventName.TriggerRatingDialog,
    handler: generateHandler(PostMessageEventName.TriggerRatingDialog)
  },
  setUserId: {
    name: PostMessageEventName.SetUserId,
    handler: generateHandler(PostMessageEventName.SetUserId)
  },
  getNativeNotificationPermission: {
    name: PostMessageEventName.GetNativeNotificationPermission,
    handler: generateHandler(PostMessageEventName.GetNativeNotificationPermission)
  },
  showNativeNotificationPermissionDialog: {
    name: PostMessageEventName.ShowNativeNotificationPermissionDialog,
    handler: generateHandler(PostMessageEventName.ShowNativeNotificationPermissionDialog)
  },
  getDeviceToken: {
    name: PostMessageEventName.GetDeviceToken,
    handler: generateHandler(PostMessageEventName.GetDeviceToken)
  },
  getDeviceInfo: {
    name: PostMessageEventName.GetDeviceInfo,
    handler: generateHandler(PostMessageEventName.GetDeviceInfo)
  }
  //  FixMe: areShortcutsEnabled and createShortcut are not available via context in the current implementation
};
const NativeContext = /*#__PURE__*/createContext(null);
const isNativeFlutterAndroidApp = () => {
  // JS channel is only supported in new native Flutter app
  return typeof window !== 'undefined' && window.native_android;
};
const isNativeFlutterIOSApp = () => {
  // JS channel is only supported in new native Flutter app
  return typeof window !== 'undefined' && window.native_ios;
};
const isNativeFlutterApp = () => {
  return !!(isNativeFlutterAndroidApp() || isNativeFlutterIOSApp());
};
const isLegacyAndroidApp = () => {
  return windowIsDefined && window.native && !isNativeFlutterApp();
};
const isLegacyIOSApp = () => {
  var _window$webkit2;
  return windowIsDefined && ((_window$webkit2 = window.webkit) === null || _window$webkit2 === void 0 || (_window$webkit2 = _window$webkit2.messageHandlers) === null || _window$webkit2 === void 0 ? void 0 : _window$webkit2.native);
};
const isAndroid = isLegacyAndroidApp() || isNativeFlutterAndroidApp();
const isIos = isLegacyIOSApp() || isNativeFlutterIOSApp();
const getPlatform = () => {
  if (isAndroid) {
    return 'android';
  }
  if (isIos) {
    return 'ios';
  }
  return 'web';
};
export const NativeProvider = _ref => {
  let {
    children
  } = _ref;
  const [buildInfo, setBuildInfo] = useState({
    platform: '',
    version: '',
    build: ''
  });
  useEffect(() => {
    window.setDeviceInfo = (platform, version, build) => {
      setBuildInfo({
        platform,
        version,
        build
      });
    };
    availableHandlers.getDeviceInfo.handler();
  }, []);
  const nativeInfo = {
    isNativeApp: !!isNativeApp(),
    // false for web, true for legacy native apps ios and android, true for flutter apps
    isFlutter: isNativeFlutterApp(),
    // false for legacy native apps ios and android, true for flutter apps
    platform: getPlatform(),
    buildInfo: buildInfo,
    ...availableHandlers
  };
  return /*#__PURE__*/React.createElement(NativeContext.Provider, {
    value: nativeInfo,
    "data-sentry-element": "unknown",
    "data-sentry-component": "NativeProvider",
    "data-sentry-source-file": "NativeContext.tsx"
  }, children);
};
export const useNative = () => {
  return useContext(NativeContext);
};